/**
 * ⏰ [Phase 56.5 — 2026-07-09] Panneau sticky-right "Booking incohérent"
 * extrait depuis CalendrierProduction.jsx (bloc legacy ~L1643-1770).
 *
 * Rôle : liste les BT à venir dont la durée cédulée diverge de la
 * recommandation (heures budgetées / heures par jour / nb membres). Chips de
 * raison + boutons "Mettre au recommandé", ±1 jour, "Ouvrir BT".
 *
 * Contrat :
 *   - `alerts` : liste bookingAlerts
 *   - `actionLoadingId` : bon_id en cours d'action (désactive boutons)
 *   - `onClose()`
 *   - `onAdjust(alert, deltaDays)` : appelle applyBookingAdjustment
 *   - `onOpenBT(alert)` : navigate vers la page BT
 *
 * Aucun changement fonctionnel — pur split JSX.
 */
import { Clock, X, Plus, Minus } from 'lucide-react';

export default function BookingAlertsPanel({
  alerts = [],
  actionLoadingId,
  onClose,
  onAdjust,
  onOpenBT,
}) {
  return (
    <div className="fixed right-0 top-0 h-full w-96 bg-white shadow-2xl border-l border-orange-200 z-50 flex flex-col" data-testid="booking-alerts-panel">
      <div className="bg-gradient-to-br from-orange-50 to-orange-100 border-b border-orange-200 px-4 py-3 flex items-start justify-between sticky top-0 z-10 shrink-0">
        <div className="min-w-0 pr-2">
          <h2 className="font-semibold text-orange-900 flex items-center gap-2 text-base">
            <Clock className="w-5 h-5 shrink-0" />
            <span>Booking incohérent</span>
            {alerts.length > 0 && (
              <span className="ml-1 bg-orange-600 text-white text-[11px] font-bold px-2 py-0.5 rounded-full">
                {alerts.length}
              </span>
            )}
          </h2>
          <p className="text-[11px] text-orange-700/80 mt-1 leading-relaxed">
            BT à venir dont la durée cédulée diverge de la recommandation
          </p>
        </div>
        <button
          onClick={() => onClose?.()}
          className="shrink-0 rounded-full w-8 h-8 flex items-center justify-center text-orange-700 hover:text-white hover:bg-orange-600 transition border border-orange-300 hover:border-orange-600 bg-white/60"
          aria-label="Fermer le panneau"
          data-testid="btn-close-booking-panel"
          title="Fermer"
        >
          <X className="w-4 h-4" strokeWidth={2.5} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {alerts.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            <span className="text-4xl">✅</span>
            <p className="text-sm mt-3 font-medium text-slate-600">Tous les BT à venir ont le bon nombre de jours</p>
            <p className="text-xs mt-1">Aucun écart entre cédulé et recommandé</p>
          </div>
        )}
        {alerts.map(alert => {
          const isSous = alert.categorie === 'sous_cedule';
          const ceilR = Math.ceil(alert.recommended_days);
          const targetDelta = ceilR - alert.scheduled_days;
          const loadingThis = actionLoadingId === alert.bon_id;
          const ecart = Math.abs(alert.scheduled_days - alert.recommended_days).toFixed(1);
          const raisonChips = [];
          if (isSous) {
            raisonChips.push({ label: 'Durée prévue trop courte', cls: 'bg-red-100 text-red-700 border-red-300' });
            if ((alert.heures_par_jour || 8) > 8) {
              raisonChips.push({ label: `Charge >${alert.heures_par_jour}h/jour`, cls: 'bg-amber-100 text-amber-700 border-amber-300' });
            }
            if ((alert.nb_membres_equipe || 1) < 2) {
              raisonChips.push({ label: 'Équipe réduite', cls: 'bg-amber-100 text-amber-700 border-amber-300' });
            }
          } else {
            raisonChips.push({ label: 'Durée prévue trop longue', cls: 'bg-amber-100 text-amber-700 border-amber-300' });
            if ((alert.nb_membres_equipe || 1) >= 3) {
              raisonChips.push({ label: 'Équipe nombreuse', cls: 'bg-blue-100 text-blue-700 border-blue-300' });
            }
          }
          return (
            <div key={alert.bon_id} className={`border rounded-lg overflow-hidden ${isSous ? 'border-red-200 bg-red-50/30' : 'border-amber-200 bg-amber-50/30'}`} data-testid={`booking-alert-${alert.bon_id}`}>
              <div className="px-3 py-2.5 border-b bg-white/60">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{alert.dossier_nom}</p>
                    <p className="text-[11px] text-slate-500 truncate">
                      {alert.chef_equipe_nom} · {alert.type_travaux || '—'} · {alert.first_date}
                    </p>
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${isSous ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                    {isSous ? `−${ecart}j` : `+${ecart}j`}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {raisonChips.map((c, i) => (
                    <span key={i} className={`text-[10px] font-semibold rounded border px-1.5 py-0.5 ${c.cls}`}>
                      {c.label}
                    </span>
                  ))}
                </div>
                <div className="mt-1.5 text-[11px] text-slate-600 leading-relaxed">
                  <div>Cédulé : <b>{alert.scheduled_days}j</b> · Recommandé : <b>{alert.recommended_days.toFixed(1)}j</b></div>
                  <div className="text-slate-400 mt-0.5">
                    Calcul : {alert.heures_budgetees.toFixed(1)}h ÷ {alert.heures_par_jour}h/jour ÷ {alert.nb_membres_equipe} pers.
                  </div>
                </div>
              </div>
              <div className="px-3 py-2 flex flex-wrap gap-1.5">
                {targetDelta !== 0 && (
                  <button
                    disabled={loadingThis}
                    onClick={() => onAdjust?.(alert, targetDelta)}
                    className="text-[11px] font-semibold text-emerald-700 hover:text-emerald-900 bg-emerald-50 hover:bg-emerald-100 rounded px-2 py-1 transition disabled:opacity-50"
                    data-testid={`btn-booking-recommande-${alert.bon_id}`}
                    title={`Mettre à ${ceilR}j (recommandé)`}
                  >
                    ✓ Mettre au recommandé ({ceilR}j)
                  </button>
                )}
                <button
                  disabled={loadingThis || alert.scheduled_days <= 1}
                  onClick={() => onAdjust?.(alert, -1)}
                  className="text-[11px] font-semibold text-slate-700 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 rounded px-2 py-1 transition disabled:opacity-50 flex items-center gap-1"
                  data-testid={`btn-booking-minus-${alert.bon_id}`}
                >
                  <Minus className="w-3 h-3" /> 1 jour
                </button>
                <button
                  disabled={loadingThis}
                  onClick={() => onAdjust?.(alert, 1)}
                  className="text-[11px] font-semibold text-slate-700 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 rounded px-2 py-1 transition disabled:opacity-50 flex items-center gap-1"
                  data-testid={`btn-booking-plus-${alert.bon_id}`}
                >
                  <Plus className="w-3 h-3" /> 1 jour
                </button>
                <button
                  disabled={loadingThis}
                  onClick={() => onOpenBT?.(alert)}
                  className="text-[11px] font-semibold text-blue-700 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 rounded px-2 py-1 transition ml-auto"
                  data-testid={`btn-booking-open-${alert.bon_id}`}
                >
                  Ouvrir BT
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
